import type { ButtonHTMLAttributes, Ref } from "react";

import { button, buttonSize } from "~/components/Button.css";

export type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";
export type ButtonSize = "sm" | "md";

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual variant. Defaults to "secondary" (the safest default — a
   * primary button should be a deliberate choice, not the fallthrough).
   */
  variant?: ButtonVariant;
  /**
   * Control size. Defaults to "md". Use "sm" on dense console surfaces
   * (action bars, table row actions) where a full-height control would
   * dominate the row.
   */
  size?: ButtonSize;
  ref?: Ref<HTMLButtonElement>;
}

/**
 * Thin wrapper around a native <button>. Adds a variant-driven class
 * name from the token layer and forwards everything else to the
 * underlying element. No state, no effects — just typed styling.
 *
 * Callers get full access to the native button API: type="submit",
 * onClick, disabled, aria-*, form, formMethod, autoFocus, all of it.
 * The component intentionally does NOT default `type="button"`: that
 * bites callers who want a submit button inside a form. If you're
 * writing a non-submit button outside a form, pass type="button"
 * explicitly — React will yell in dev if you miss it inside a form.
 */
export function Button({
  variant = "secondary",
  size = "md",
  className,
  ref,
  ...rest
}: ButtonProps) {
  const merged = [button[variant], buttonSize[size], className]
    .filter((c) => c !== undefined && c.length > 0)
    .join(" ");
  return <button ref={ref} className={merged} {...rest} />;
}
